/**
 * transactions.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin transaction management — session-gated, full sidebar, design-matched
 * to wallets.jsx / AdminDashboard.
 * Route: /admin/transactions
 *
 * Tables used:
 *   transactions  (id, type, amount, status, note, reference, created_at,
 *                  user_id, wallet_id, payment_method_id)
 *   users         (id, first_name, last_name, email)
 *   wallets       (id, balance, currency)
 *   payment_methods (type)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut,CreditCard, MapPin,
  // page
  Search, RefreshCw, Plus, X, AlertCircle,
  CheckCircle2, XCircle, Clock, Eye,
  ChevronUp, ChevronDown, ArrowUpRight, ArrowDownRight,
  Filter, DollarSign, TrendingUp, TrendingDown, Users,
} from "lucide-react";

const PER_PAGE = 10;

// ─── Sidebar nav ─────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
   {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
    ],
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --border-glow:  rgba(99,102,241,0.3);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
    --transition-smooth: cubic-bezier(0.4, 0, 0.2, 1);
  }

  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; scroll-behavior: smooth; }

  /* Shell */
  .tx-shell {
    display: flex; height: 100vh; overflow: hidden;
    background: var(--bg-base); position: relative;
  }
  .tx-shell::before {
    content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* Sidebar */
  .ad-sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    display: flex; flex-direction: column;
    background: var(--bg-surface); border-right: 1px solid var(--border);
    position: relative; z-index: 10;
    height: 100vh; overflow: hidden;
  }
  .ad-sidebar::after {
    content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }
  .ad-logo {
    display: flex; align-items: center; gap: 11px;
    padding: 0 18px; height: var(--topbar-h);
    border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .ad-logo-icon {
    width: 32px; height: 32px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35), 0 0 32px rgba(99,102,241,0.15);
  }
  .ad-logo-text strong {
    font-size: 13.5px; font-weight: 800; letter-spacing: -0.4px;
    background: linear-gradient(to right, #eeeeff, #a5b4fc);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .ad-logo-text span {
    font-size: 9.5px; color: var(--text-muted); display: block;
    margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500;
  }
  .ad-nav {
    flex: 1; overflow-y: auto; overflow-x: hidden;
    padding: 18px 10px; display: flex; flex-direction: column; gap: 22px;
    scroll-behavior: smooth; -webkit-overflow-scrolling: touch;
    min-height: 0;
  }
  .ad-nav::-webkit-scrollbar { width: 3px; }
  .ad-nav::-webkit-scrollbar-track { background: transparent; }
  .ad-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.2); border-radius: 99px; }
  .ad-nav::-webkit-scrollbar-thumb:hover { background: rgba(99,102,241,0.4); }
  .ad-nav-section-label {
    font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--text-muted); padding: 0 10px; margin-bottom: 5px;
  }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 10px; border-radius: var(--radius-sm);
    font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    text-decoration: none; transition: all 0.18s ease; position: relative;
  }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%);
    color: var(--accent-light); font-weight: 600;
    box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15);
  }
  .ad-nav-link.active::before {
    content: ''; position: absolute; left: 0; top: 18%; bottom: 18%;
    width: 2.5px; border-radius: 99px;
    background: linear-gradient(to bottom, var(--accent), var(--purple));
    box-shadow: 0 0 10px var(--accent-glow), 0 0 20px rgba(99,102,241,0.2);
  }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }
  .ad-user-card {
    flex-shrink: 0; border-top: 1px solid var(--border); padding: 12px 10px;
    background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%);
  }
  .ad-user-inner {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 10px; border-radius: var(--radius-sm); transition: background 0.15s;
  }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2));
    border: 1.5px solid rgba(99,102,241,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: var(--accent-light); flex-shrink: 0;
  }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span   { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn {
    width: 28px; height: 28px; border-radius: 7px;
    background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); transition: all 0.15s;
  }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); }

  /* Page column */
  .tx-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; }

  /* Topbar */
  .tx-topbar {
    flex-shrink: 0; height: var(--topbar-h);
    background: rgba(10,10,22,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; position: relative;
  }
  .tx-topbar::after {
    content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%);
  }
  .tx-topbar-left  { display: flex; align-items: center; gap: 14px; }
  .tx-topbar-badge {
    display: flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px;
    background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2);
    font-size: 10px; font-weight: 700; color: var(--accent-light);
    letter-spacing: 0.5px; text-transform: uppercase;
  }
  .tx-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .tx-topbar-sub   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
  .tx-topbar-right { display: flex; align-items: center; gap: 8px; }

  /* Buttons */
  .tx-btn {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px;
    border-radius: var(--radius-sm); font-size: 11.5px; font-weight: 500;
    cursor: pointer; transition: all 0.18s; font-family: var(--font-sans);
    border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary);
  }
  .tx-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }
  .tx-btn-primary {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px;
    border-radius: var(--radius-sm); font-size: 11.5px; font-weight: 600;
    cursor: pointer; transition: all 0.18s; font-family: var(--font-sans);
    border: none; background: var(--accent); color: #fff;
  }
  .tx-btn-primary:hover { background: #4f52e0; box-shadow: 0 0 20px rgba(99,102,241,0.35); }

  /* Main */
  .tx-main {
    flex: 1; overflow-y: auto; padding: 26px 28px;
    display: flex; flex-direction: column; gap: 20px;
  }
  .tx-main::-webkit-scrollbar { width: 4px; }
  .tx-main::-webkit-scrollbar-track { background: transparent; }
  .tx-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat grid */
  .tx-stat-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; }
  .tx-stat {
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: var(--radius-md); padding: 18px 20px;
    display: flex; flex-direction: column; gap: 12px;
    position: relative; overflow: hidden;
    transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s;
  }
  .tx-stat::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%);
    pointer-events: none;
  }
  .tx-stat::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%);
    pointer-events: none;
  }
  .tx-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .tx-stat-top   { display: flex; align-items: flex-start; justify-content: space-between; }
  .tx-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .tx-stat-icon  { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .tx-stat-value { font-size: 22px; font-weight: 700; color: var(--text-primary); letter-spacing: -1px; font-family: var(--font-mono); line-height: 1; }
  .tx-stat-sub   { font-size: 10.5px; color: var(--text-muted); margin-top: 4px; }

  /* Pending banner */
  .tx-pending-banner {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 18px; border-radius: var(--radius-sm);
    background: var(--amber-dim); border: 1px solid rgba(251,191,36,0.22);
  }
  .tx-pending-banner p { font-size: 12.5px; color: var(--amber); flex: 1; }
  .tx-pending-banner strong { font-weight: 700; }
  .tx-pending-link {
    font-size: 11px; color: var(--amber); background: none; border: none;
    cursor: pointer; text-decoration: underline; font-family: var(--font-sans);
    white-space: nowrap;
  }
  .tx-pending-link:hover { text-decoration: none; }

  /* Panel */
  .tx-panel { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }

  /* Toolbar */
  .tx-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; padding: 14px 20px; border-bottom: 1px solid var(--border); flex-wrap: wrap;
  }
  .tx-toolbar-left  { display: flex; align-items: center; gap: 10px; }
  .tx-toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .tx-panel-title { font-size: 13px; font-weight: 700; color: var(--text-primary); }
  .tx-count-pill {
    padding: 2px 8px; border-radius: 99px; border: 1px solid var(--border-hi);
    font-size: 10px; color: var(--text-muted); font-family: var(--font-mono);
  }

  /* Filters */
  .tx-select {
    padding: 5px 10px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 11.5px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s; cursor: pointer;
  }
  .tx-select:focus { border-color: rgba(99,102,241,0.4); }
  .tx-search-wrap { position: relative; }
  .tx-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .tx-search {
    padding: 6px 12px 6px 30px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary);
    font-family: var(--font-sans); width: 200px; outline: none; transition: border-color 0.18s;
  }
  .tx-search::placeholder { color: var(--text-muted); }
  .tx-search:focus { border-color: rgba(99,102,241,0.4); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .tx-table-wrap { overflow-x: auto; }
  .tx-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .tx-table thead tr { border-bottom: 1px solid var(--border); }
  .tx-table th {
    padding: 10px 18px; text-align: left;
    font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase;
    color: var(--text-muted); white-space: nowrap; cursor: pointer; user-select: none; transition: color 0.15s;
  }
  .tx-table th:hover { color: var(--text-secondary); }
  .tx-table th.no-sort { cursor: default; }
  .tx-table tbody tr { border-bottom: 1px solid rgba(255,255,255,0.035); transition: background 0.12s; }
  .tx-table tbody tr:last-child { border-bottom: none; }
  .tx-table tbody tr:hover { background: rgba(99,102,241,0.04); }
  .tx-table td { padding: 13px 18px; vertical-align: middle; }

  /* ID cell */
  .tx-id { font-size: 11px; font-family: var(--font-mono); color: var(--text-muted); }

  /* Avatar cell */
  .tx-avatar-cell { display: flex; align-items: center; gap: 10px; }
  .tx-avatar {
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700; border: 1.5px solid rgba(99,102,241,0.2);
  }
  .tx-avatar-indigo { background: rgba(99,102,241,0.15);  color: #818cf8; }
  .tx-avatar-green  { background: rgba(52,211,153,0.15);  color: #34d399; }
  .tx-avatar-amber  { background: rgba(251,191,36,0.15);  color: #fbbf24; }
  .tx-avatar-rose   { background: rgba(251,113,133,0.15); color: #fb7185; }
  .tx-avatar-purple { background: rgba(167,139,250,0.15); color: #a78bfa; }
  .tx-cell-name  { font-size: 12.5px; font-weight: 500; color: var(--text-primary); }
  .tx-cell-email { font-size: 10.5px; color: var(--text-muted); margin-top: 1px; }

  /* Amount */
  .tx-amount-pos { font-size: 14px; font-weight: 700; color: var(--green);  font-family: var(--font-mono); }
  .tx-amount-neg { font-size: 14px; font-weight: 700; color: var(--rose);   font-family: var(--font-mono); }

  /* Type badge */
  .tx-type-deposit    { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: var(--green); }
  .tx-type-withdrawal { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: var(--rose);  }

  /* Status chip */
  .tx-chip {
    display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 99px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.4px; text-transform: capitalize; border: 1px solid;
  }
  .tx-chip.pending   { background: var(--amber-dim); color: var(--amber); border-color: rgba(251,191,36,0.25);   }
  .tx-chip.approved  { background: var(--green-dim); color: var(--green); border-color: rgba(52,211,153,0.25);   }
  .tx-chip.cancelled { background: var(--rose-dim);  color: var(--rose);  border-color: rgba(251,113,133,0.25);  }

  /* Date */
  .tx-date { font-size: 11px; color: var(--text-muted); font-family: var(--font-mono); white-space: nowrap; }

  /* Action buttons */
  .tx-actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }
  .tx-icon-btn {
    width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
    background: none; border: 1px solid var(--border); color: var(--text-muted);
    cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .tx-icon-btn:hover { background: var(--bg-hover); color: var(--text-primary); border-color: var(--border-hi); }
  .tx-icon-btn.approve:hover { background: var(--green-dim); color: var(--green); border-color: rgba(52,211,153,0.3);  }
  .tx-icon-btn.cancel:hover  { background: var(--rose-dim);  color: var(--rose);  border-color: rgba(251,113,133,0.3); }

  /* Skeleton */
  .tx-skel {
    height: 10px; border-radius: 5px;
    background: linear-gradient(90deg, var(--bg-hover) 25%, rgba(99,102,241,0.05) 50%, var(--bg-hover) 75%);
    background-size: 200% 100%; animation: tx-shimmer 1.8s infinite;
  }
  @keyframes tx-shimmer { 0%{ background-position:200% 0 } 100%{ background-position:-200% 0 } }
  .tx-spin { animation: tx-spin 0.9s linear infinite; }
  @keyframes tx-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  /* Empty */
  .tx-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px; gap: 8px; color: var(--text-muted); font-size: 12px; }

  /* Pagination */
  .tx-pagination {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 20px; border-top: 1px solid var(--border);
  }
  .tx-pag-info  { font-size: 11px; color: var(--text-muted); font-family: var(--font-mono); }
  .tx-pag-pages { display: flex; gap: 4px; }
  .tx-pag-btn {
    width: 28px; height: 28px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-family: var(--font-mono);
    border: 1px solid var(--border); background: none;
    color: var(--text-muted); cursor: pointer; transition: all 0.15s;
  }
  .tx-pag-btn:hover  { background: var(--bg-hover); color: var(--text-primary); }
  .tx-pag-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Modal */
  .tx-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.72);
    display: flex; align-items: center; justify-content: center;
    z-index: 50; padding: 16px; backdrop-filter: blur(4px);
  }
  .tx-modal {
    background: var(--bg-elevated); border: 1px solid var(--border-hi);
    border-radius: var(--radius-lg); width: 100%; max-width: 480px;
    display: flex; flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1);
  }
  .tx-modal-head {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 20px 24px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .tx-modal-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .tx-modal-sub   { font-size: 12px; color: var(--text-secondary); margin-top: 3px; }
  .tx-modal-body  { padding: 22px 24px; display: flex; flex-direction: column; gap: 16px; overflow-y: auto; max-height: 70vh; }
  .tx-modal-foot  {
    display: flex; align-items: center; justify-content: flex-end; gap: 8px;
    padding: 16px 24px; border-top: 1px solid var(--border);
  }
  .tx-close-btn {
    width: 28px; height: 28px; border-radius: 7px; background: none; border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .tx-close-btn:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.25); }

  /* Detail grid */
  .tx-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .tx-detail-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 5px; }
  .tx-detail-val   { font-size: 12.5px; color: var(--text-primary); }
  .tx-detail-hr { border: none; border-top: 1px solid var(--border); }
  .tx-user-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); }

  /* Note box */
  .tx-note-box { padding: 10px 14px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); font-size: 12px; color: var(--text-secondary); line-height: 1.5; }

  /* Form elements */
  .tx-label { font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; display: block; }
  .tx-input {
    width: 100%; padding: 9px 12px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s;
  }
  .tx-input::placeholder { color: var(--text-muted); }
  .tx-input:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .tx-input.has-prefix { padding-left: 24px; }
  .tx-input-wrap { position: relative; }
  .tx-input-prefix { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 13px; color: var(--text-muted); pointer-events: none; }
  .tx-select-full {
    width: 100%; padding: 9px 12px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 13px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s; cursor: pointer;
  }
  .tx-select-full:focus { border-color: rgba(99,102,241,0.45); }
  .tx-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

  /* Error */
  .tx-error {
    display: flex; align-items: center; gap: 8px; padding: 10px 14px;
    border-radius: var(--radius-sm); background: rgba(251,113,133,0.08);
    border: 1px solid rgba(251,113,133,0.22); color: var(--rose); font-size: 12px;
  }

  /* Ghost / submit buttons */
  .tx-ghost-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 16px;
    border-radius: var(--radius-sm); background: none; border: 1px solid var(--border-hi);
    color: var(--text-secondary); font-size: 12px; font-weight: 500;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .tx-ghost-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .tx-submit-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 18px;
    border-radius: var(--radius-sm); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans); border: none;
    background: var(--accent); color: #fff;
  }
  .tx-submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .tx-submit-btn:not(:disabled):hover { background: #4f52e0; box-shadow: 0 0 16px rgba(99,102,241,0.4); }
  .tx-approve-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 16px;
    border-radius: var(--radius-sm); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans); border: none;
    background: var(--green); color: #0a0a0a;
  }
  .tx-approve-btn:not(:disabled):hover { box-shadow: 0 0 16px rgba(52,211,153,0.4); }
  .tx-approve-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .tx-cancel-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 16px;
    border-radius: var(--radius-sm); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; font-family: var(--font-sans); border: none;
    background: var(--rose-dim); color: var(--rose); border: 1px solid rgba(251,113,133,0.25);
  }
  .tx-cancel-btn:not(:disabled):hover { background: rgba(251,113,133,0.2); }
  .tx-cancel-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Toast */
  .tx-toast {
    position: fixed; bottom: 24px; right: 24px; z-index: 100;
    display: flex; align-items: center; gap: 10px; padding: 12px 18px;
    border-radius: var(--radius-md); background: var(--bg-elevated);
    border: 1px solid var(--border-hi);
    box-shadow: 0 8px 32px rgba(0,0,0,0.5);
    font-size: 13px; color: var(--text-primary);
    animation: tx-slide-in 0.22s ease;
  }
  .tx-toast.ok  { border-color: rgba(52,211,153,0.35); }
  .tx-toast.err { border-color: rgba(251,113,133,0.35); }
  @keyframes tx-slide-in { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }

  /* Gate */
  .tx-gate { height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px; background: var(--bg-base); }
  .tx-gate-icon { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg,#6366f1,#a78bfa); box-shadow: 0 0 24px rgba(99,102,241,0.4); display: flex; align-items: center; justify-content: center; }

  /* ═══════════════════════════════════════
     MOBILE SIDEBAR OVERLAY + HAMBURGER
  ════════════════════════════════════════ */
  .ad-sidebar-overlay {
    display: none;
    position: fixed; inset: 0; background: rgba(0,0,0,0.6);
    z-index: 40; backdrop-filter: blur(2px);
    animation: overlay-fade-in 0.22s var(--transition-smooth);
  }
  @keyframes overlay-fade-in { from { opacity:0 } to { opacity:1 } }

  .tx-hamburger {
    display: none;
    width: 36px; height: 36px; border-radius: var(--radius-sm);
    background: var(--bg-card); border: 1px solid var(--border-hi);
    color: var(--text-secondary); cursor: pointer;
    align-items: center; justify-content: center;
    transition: all 0.15s; flex-shrink: 0;
  }
  .tx-hamburger:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* ═══════════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ════════════════════════════════════════ */

  /* ── Large desktop (≥1400px) ── */
  @media (min-width: 1400px) {
    .tx-stat-grid { gap: 18px; }
    .tx-main { padding: 32px 36px; }
    .tx-topbar { padding: 0 36px; }
    .tx-stat-value { font-size: 26px; }
  }

  /* ── Medium desktop (1024px – 1279px) ── */
  @media (max-width: 1279px) {
    :root { --sidebar-w: 210px; }
    .tx-main { padding: 22px 22px; }
    .tx-topbar { padding: 0 22px; }
    .tx-stat-grid { gap: 12px; }
  }

  /* ── Desktop (≥1024px): transaction table scrolls independently,
        header + toolbar + pagination stay in place ── */
  @media (min-width: 1024px) {
    .tx-table-wrap {
      max-height: 58vh;
      overflow-y: auto;
    }
    .tx-table-wrap::-webkit-scrollbar { width: 4px; }
    .tx-table-wrap::-webkit-scrollbar-track { background: transparent; }
    .tx-table-wrap::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }
    .tx-table thead th {
      position: sticky; top: 0; z-index: 2;
      background: var(--bg-card);
    }
  }

  /* ── Tablet landscape (768px – 1023px): icon-strip sidebar ── */
  @media (max-width: 1023px) {
    :root { --sidebar-w: 60px; }

    .ad-logo-text,
    .ad-nav-section-label,
    .ad-nav-link span,
    .ad-nav-chevron,
    .ad-user-info,
    .ad-logout-btn { display: none !important; }

    .ad-logo { justify-content: center; padding: 0; }
    .ad-logo-icon { width: 36px; height: 36px; }
    .ad-nav { padding: 14px 6px; gap: 14px; }
    .ad-nav-items { gap: 2px; }
    .ad-nav-link { justify-content: center; padding: 10px; gap: 0; }
    .ad-nav-link svg { opacity: 0.7; }
    .ad-nav-link.active svg { opacity: 1; }
    .ad-nav-link.active::before { top: 10%; bottom: 10%; }
    .ad-user-card { padding: 10px 6px; }
    .ad-user-inner { justify-content: center; padding: 8px; gap: 0; }

    .tx-stat-grid { grid-template-columns: repeat(3, 1fr); }
    .tx-main { padding: 18px 18px; gap: 16px; }
    .tx-topbar { padding: 0 18px; }
    .tx-toolbar { flex-direction: column; align-items: stretch; gap: 10px; }
    .tx-toolbar-right { flex-wrap: wrap; }
    .tx-search { width: 100%; }
  }

  /* ── Mobile (≤767px): off-canvas drawer ── */
  @media (max-width: 767px) {
    :root { --sidebar-w: 232px; }

    .tx-hamburger { display: flex; }

    .ad-sidebar {
      position: fixed; top: 0; left: 0; height: 100vh;
      z-index: 50; transform: translateX(-100%);
      transition: transform 0.28s var(--transition-smooth);
      box-shadow: 4px 0 40px rgba(0,0,0,0.5);
      overflow: hidden;
    }
    .ad-sidebar.open { transform: translateX(0); }
    .ad-sidebar-overlay.open { display: block; }

    /* Restore all hidden items inside the open mobile drawer */
    .ad-sidebar.open .ad-logo-text,
    .ad-sidebar.open .ad-nav-section-label,
    .ad-sidebar.open .ad-nav-link span,
    .ad-sidebar.open .ad-nav-chevron,
    .ad-sidebar.open .ad-user-info,
    .ad-sidebar.open .ad-logout-btn { display: initial !important; }

    .ad-sidebar.open .ad-sidebar-close { display: flex !important; }
    .ad-sidebar.open .ad-logo { justify-content: flex-start; padding: 0 18px; }
    .ad-sidebar.open .ad-nav { padding: 18px 10px; gap: 22px; }
    .ad-sidebar.open .ad-nav-link { justify-content: flex-start; padding: 8px 10px; gap: 9px; }
    .ad-sidebar.open .ad-nav-link span { display: block !important; flex: 1; }
    .ad-sidebar.open .ad-nav-section-label { display: block !important; }
    .ad-sidebar.open .ad-user-inner { justify-content: flex-start; gap: 10px; padding: 9px 10px; }

    .tx-shell { overflow: hidden; }
    .tx-page { width: 100%; }

    .tx-stat-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .tx-stat-value { font-size: 18px; }
    .tx-stat { padding: 14px; }
    .tx-stat-icon { width: 28px; height: 28px; }

    .tx-main { padding: 14px 12px; gap: 14px; }
    .tx-topbar { padding: 0 14px; }
    .tx-topbar-sub { display: none; }
    .tx-topbar-title { font-size: 13px; }
    .tx-topbar-badge { display: none; }

    .tx-toolbar { padding: 10px 14px; flex-direction: column; align-items: stretch; gap: 10px; }
    .tx-toolbar-right { flex-direction: column; align-items: stretch; gap: 8px; }
    .tx-search { width: 100%; }
    .tx-select { width: 100%; }

    /* Hide ID and email columns on mobile */
    .tx-table th:nth-child(1), .tx-table td:nth-child(1),
    .tx-table th:nth-child(6), .tx-table td:nth-child(6) { display: none; }
    .tx-cell-email { display: none; }
    .tx-table th, .tx-table td { padding: 10px 10px; }

    .tx-overlay { align-items: flex-end; padding: 0; }
    .tx-modal { border-radius: var(--radius-md) var(--radius-md) 0 0; max-width: 100%; }
    .tx-modal-foot { flex-wrap: wrap; gap: 8px; }
    .tx-two-col { grid-template-columns: 1fr; }

    .tx-pagination { flex-direction: column; align-items: flex-start; gap: 10px; padding: 10px 14px; }
    .tx-pag-pages { flex-wrap: wrap; }

    .tx-toast { bottom: 16px; right: 12px; left: 12px; font-size: 12px; }
  }

  /* ── Small mobile (≤480px) ── */
  @media (max-width: 480px) {
    .tx-stat-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .tx-stat-value { font-size: 16px; }
    .tx-stat-sub { font-size: 9px; }
    .tx-stat { padding: 12px; gap: 8px; }

    /* Also hide Type column — keep User, Amount, Status, Actions */
    .tx-table th:nth-child(3), .tx-table td:nth-child(3) { display: none; }

    .tx-avatar { width: 28px; height: 28px; font-size: 9px; }
    .tx-avatar-cell { gap: 7px; }

    .tx-pending-banner { flex-wrap: wrap; gap: 8px; }
  }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtCurrency(val) {
  if (val == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(val);
}
function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function initials(first, last) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
}
const PALETTE = ["indigo", "green", "amber", "rose", "purple"];
function avatarColor(id) {
  if (!id) return PALETTE[0];
  const n = typeof id === "string" ? id.charCodeAt(0) + id.charCodeAt(id.length - 1) : id;
  return PALETTE[n % PALETTE.length];
}

// ─── Payment proof (private bucket) ────────────────────────────────────────────
// `transactions.proof_url` stores the object's storage PATH, not a public URL,
// because the `payment-proofs` bucket is private. Admins get a short-lived
// signed URL on demand to view or download the file.
const PAYMENT_PROOF_BUCKET = "payment-proofs";
async function getProofSignedUrl(path, { download = false } = {}) {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from(PAYMENT_PROOF_BUCKET)
    .createSignedUrl(path, 300, download ? { download: true } : undefined); // 5 min
  if (error) throw new Error(error.message);
  return data?.signedUrl ?? null;
}

/* Inline proof viewer/downloader — shown in the detail modal and reusable
   wherever a transaction row needs a quick "view proof" affordance. */
function ProofViewer({ path }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!path) { setUrl(null); return; }
    setLoading(true); setError("");
    getProofSignedUrl(path)
      .then((signed) => { if (!cancelled) setUrl(signed); })
      .catch((e) => { if (!cancelled) setError(e.message || "Could not load proof."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);

  const handleDownload = async () => {
    try {
      const signed = await getProofSignedUrl(path, { download: true });
      if (signed) window.open(signed, "_blank", "noopener");
    } catch (e) {
      setError(e.message || "Could not download proof.");
    }
  };

  if (!path) {
    return <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No proof attached</div>;
  }
  if (loading) {
    return <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 6 }}><RefreshCw size={12} className="tx-spin" />Loading proof…</div>;
  }
  if (error) {
    return <div style={{ fontSize: 12, color: "var(--rose)", display: "flex", alignItems: "center", gap: 6 }}><AlertCircle size={12} />{error}</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", borderRadius: "var(--radius-sm)", overflow: "hidden", border: "1px solid var(--border-hi)", width: 160 }}>
          <img src={url} alt="Payment proof" style={{ width: "100%", height: 110, objectFit: "cover", display: "block" }} />
        </a>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" className="tx-ghost-btn" style={{ fontSize: 11, padding: "6px 10px" }}>
            <Eye size={11} /> View full size
          </a>
        )}
        <button type="button" className="tx-ghost-btn" style={{ fontSize: 11, padding: "6px 10px" }} onClick={handleDownload}>
          <ArrowDownRight size={11} style={{ transform: "rotate(-45deg)" }} /> Download
        </button>
      </div>
    </div>
  );
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Sort icon ────────────────────────────────────────────────────────────────
function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
  return sortDir === 1
    ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
    : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
}

// ─── Status + Type badges ─────────────────────────────────────────────────────
function StatusChip({ status }) {
  const icon = status === "approved" ? <CheckCircle2 size={9} /> : status === "cancelled" ? <XCircle size={9} /> : <Clock size={9} />;
  return <span className={`tx-chip ${status}`}>{icon}{status}</span>;
}
function TypeBadge({ type }) {
  return type === "deposit"
    ? <span className="tx-type-deposit"><ArrowUpRight size={12} />Deposit</span>
    : <span className="tx-type-withdrawal"><ArrowDownRight size={12} />Withdrawal</span>;
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function DetailModal({ txn, onClose, onApprove, onCancel }) {
  const [loading, setLoading] = useState(false);
  const user   = txn.users;
  const aColor = avatarColor(txn.user_id);

  async function handleApprove() {
    setLoading(true);
    await onApprove(txn.id, txn.user_id, txn.wallet_id, txn.amount, txn.type);
    setLoading(false); onClose();
  }
  async function handleCancel() {
    setLoading(true);
    await onCancel(txn.id);
    setLoading(false); onClose();
  }

  return (
    <div className="tx-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tx-modal">
        <div className="tx-modal-head">
          <div>
            <div className="tx-modal-title">Transaction details</div>
            <div className="tx-modal-sub" style={{ fontFamily: "var(--font-mono)" }}>#{txn.id}</div>
          </div>
          <button className="tx-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="tx-modal-body">
          {/* User row */}
          <div className="tx-user-row">
            <div className={`tx-avatar tx-avatar-${aColor}`}>{initials(user?.first_name, user?.last_name)}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{user?.first_name} {user?.last_name}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{user?.email}</div>
            </div>
          </div>

          <hr className="tx-detail-hr" />

          {/* Fields grid */}
          <div className="tx-detail-grid">
            {[
              ["Type",           <TypeBadge type={txn.type} />],
              ["Status",         <StatusChip status={txn.status} />],
              ["Amount",         <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 15, color: txn.type === "deposit" ? "var(--green)" : "var(--rose)" }}>{txn.type === "deposit" ? "+" : "−"}{fmtCurrency(txn.amount)}</span>],
              ["Date",           <span className="tx-detail-val tx-date">{fmtDate(txn.created_at)}</span>],

            ].map(([label, val]) => (
              <div key={label}>
                <div className="tx-detail-label">{label}</div>
                {val}
              </div>
            ))}
          </div>

          <hr className="tx-detail-hr" />

          {/* Payment proof */}
          <div>
            <div className="tx-detail-label" style={{ marginBottom: 8 }}>Payment proof</div>
            <ProofViewer path={txn.proof_url} />
          </div>

        </div>

        <div className="tx-modal-foot">
          <button className="tx-ghost-btn" onClick={onClose}>Close</button>
          {txn.status === "pending" && (
            <>
              <button className="tx-cancel-btn" disabled={loading} onClick={handleCancel}>
                <XCircle size={12} />{loading ? "Processing…" : "Cancel"}
              </button>
              <button className="tx-approve-btn" disabled={loading} onClick={handleApprove}>
                <CheckCircle2 size={12} />{loading ? "Processing…" : "Approve"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Add Transaction Modal ────────────────────────────────────────────────────
function AddModal({ onClose, onSuccess }) {
  const [users,    setUsers]   = useState([]);
  const [wallets,  setWallets] = useState([]);
  const [userId,   setUserId]  = useState("");
  const [walletId, setWallet]  = useState("");
  const [type,     setType]    = useState("deposit");
  const [amount,   setAmount]  = useState("");
  const [currency, setCurrency]= useState("USD");
  const [status,   setStatus]  = useState("approved");
  const [loading,  setLoading] = useState(false);
  const [error,    setError]   = useState("");

  useEffect(() => {
    supabase.from("users").select("id, first_name, last_name, email").order("first_name")
      .then(({ data }) => setUsers(data ?? []));
  }, []);

  useEffect(() => {
    if (!userId) { setWallets([]); setWallet(""); setCurrency("USD"); return; }
    supabase.from("wallets").select("id, balance, currency").eq("user_id", userId)
      .then(({ data }) => {
        setWallets(data ?? []);
        if (data?.length) {
          setWallet(String(data[0].id));
          setCurrency(data[0].currency ?? "USD");
        }
      });
  }, [userId]);

  async function handleSubmit() {
    setError("");
    if (!userId || !walletId || !amount || parseFloat(amount) <= 0) {
      setError("Fill in all required fields with a valid amount."); return;
    }
    setLoading(true);
    try {
      const parsed = parseFloat(amount);
      const net_amount = type === "deposit" ? parsed : -parsed;
      const { error: txnErr } = await supabase.from("transactions").insert({
        user_id: userId, wallet_id: walletId, type, amount: parsed,
        net_amount, currency, status, created_at: new Date().toISOString(),
      });
      if (txnErr) throw txnErr;
      if (status === "approved") {
        const wallet = wallets.find((w) => String(w.id) === walletId);
        const current = wallet?.balance ?? 0;
        const newBal  = type === "deposit" ? current + parsed : Math.max(0, current - parsed);
        await supabase.from("wallets").update({ balance: newBal, updated_at: new Date().toISOString() }).eq("id", walletId);
      }
      onSuccess("Transaction added successfully.");
      onClose();
    } catch (e) {
      setError(e.message ?? "Failed to add transaction.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="tx-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tx-modal">
        <div className="tx-modal-head">
          <div>
            <div className="tx-modal-title">Add transaction</div>
            <div className="tx-modal-sub">Manually create a deposit or withdrawal</div>
          </div>
          <button className="tx-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="tx-modal-body">
          {/* User */}
          <div>
            <label className="tx-label">User <span style={{ color: "var(--rose)" }}>*</span></label>
            <select className="tx-select-full" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Select user…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name} — {u.email}</option>
              ))}
            </select>
          </div>

          {/* Wallet */}
          {wallets.length > 0 && (
            <div>
              <label className="tx-label">Wallet <span style={{ color: "var(--rose)" }}>*</span></label>
              <select className="tx-select-full" value={walletId} onChange={(e) => setWallet(e.target.value)}>
                {wallets.map((w) => (
                  <option key={w.id} value={w.id}>{w.currency ?? "USD"} wallet — {fmtCurrency(w.balance)}</option>
                ))}
              </select>
            </div>
          )}
          {userId && wallets.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--amber)", padding: "8px 12px", background: "var(--amber-dim)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(251,191,36,0.2)" }}>
              This user has no wallet yet. Create one from the Wallets page first.
            </div>
          )}

          {/* Type + Status */}
          <div className="tx-two-col">
            <div>
              <label className="tx-label">Type</label>
              <select className="tx-select-full" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
              </select>
            </div>
            <div>
              <label className="tx-label">Status</label>
              <select className="tx-select-full" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Amount + Currency */}
          <div className="tx-two-col">
            <div>
              <label className="tx-label">Amount <span style={{ color: "var(--rose)" }}>*</span></label>
              <div className="tx-input-wrap">
                <span className="tx-input-prefix">$</span>
                <input
                  type="number" min="0" step="0.01" value={amount}
                  onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                  className="tx-input has-prefix" style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>
            </div>
            <div>
              <label className="tx-label">Currency</label>
              <select
                className="tx-select-full"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {["USD","EUR","GBP","CAD","AUD","JPY","CHF","NGN","GHS","ZAR","KES","BTC","ETH","USDT"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="tx-error"><AlertCircle size={13} style={{ flexShrink: 0 }} />{error}</div>
          )}
        </div>

        <div className="tx-modal-foot">
          <button className="tx-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="tx-submit-btn" disabled={loading || !walletId} onClick={handleSubmit}>
            <Plus size={12} />{loading ? "Adding…" : "Add transaction"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Transactions() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [sessionReady,  setSessionReady]  = useState(false);

  const [txns,         setTxns]        = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [search,       setSearch]      = useState("");
  const [statusFilter, setStatusFilter]= useState("");
  const [typeFilter,   setTypeFilter]  = useState("");
  const [sortField,    setSortField]   = useState("created_at");
  const [sortDir,      setSortDir]     = useState(-1);
  const [page,         setPage]        = useState(1);
  const [selected,     setSelected]    = useState(null);
  const [showAdd,      setShowAdd]     = useState(false);
  const [toast,        setToast]       = useState({ msg: "", ok: true });
  const [sidebarOpen,  setSidebarOpen] = useState(false);

  // ── Session gate ──────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchTxns = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch transactions with only the users join (most reliable FK)
      // wallets and payment_methods fetched separately to avoid 400s from unresolved FKs
      let { data: txnRows, error: txnErr } = await supabase
        .from("transactions")
        .select("id, type, amount, status, currency, created_at, user_id, wallet_id, proof_url")
        .order("created_at", { ascending: false });

      // If the proof_url column hasn't been migrated yet, fall back so the
      // whole page doesn't break — just without receipts until it's added.
      if (txnErr && /proof_url/i.test(txnErr.message || "")) {
        ({ data: txnRows, error: txnErr } = await supabase
          .from("transactions")
          .select("id, type, amount, status, currency, created_at, user_id, wallet_id")
          .order("created_at", { ascending: false }));
        if (!txnErr) {
          showToast("Payment proofs aren't showing — run payment_proof_setup.sql to add the proof_url column.", false);
        }
      }
      if (txnErr) throw txnErr;

      // Fetch users and wallets in parallel, merge in by id
      const userIds   = [...new Set((txnRows ?? []).map((t) => t.user_id).filter(Boolean))];
      const walletIds = [...new Set((txnRows ?? []).map((t) => t.wallet_id).filter(Boolean))];
      // payment_method_id excluded — column may not exist or RLS blocks payment_methods table

      const [{ data: userRows }, { data: walletRows }] = await Promise.all([
        userIds.length
          ? supabase.from("users").select("id, first_name, last_name, email").in("id", userIds)
          : Promise.resolve({ data: [] }),
        walletIds.length
          ? supabase.from("wallets").select("id, balance, currency").in("id", walletIds)
          : Promise.resolve({ data: [] }),
      ]);

      const userMap   = {};
      const walletMap = {};
      (userRows   ?? []).forEach((u) => { userMap[u.id]   = u; });
      (walletRows ?? []).forEach((w) => { walletMap[w.id] = w; });

      const merged = (txnRows ?? []).map((t) => ({
        ...t,
        users:           userMap[t.user_id]     ?? null,
        wallets:         walletMap[t.wallet_id] ?? null,
        payment_methods: null,
      }));

      setTxns(merged);
    } catch (e) {
      console.error("[Transactions] fetch error:", e.message);
      showToast("Failed to load transactions.", false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (sessionReady) fetchTxns(); }, [sessionReady, fetchTxns]);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3200);
  }

  // ── Approve ───────────────────────────────────────────────────────────────
  async function approveTransaction(id, userId, walletId, amount, type) {
    const { error: txnErr } = await supabase.from("transactions").update({ status: "approved" }).eq("id", id);
    if (txnErr) { showToast("Failed to approve.", false); return; }
    const wallet = txns.find((t) => t.id === id)?.wallets;
    if (wallet) {
      const current = wallet.balance ?? 0;
      const newBal  = type === "deposit" ? current + amount : Math.max(0, current - amount);
      await supabase.from("wallets").update({ balance: newBal, updated_at: new Date().toISOString() }).eq("id", walletId);
    }
    setTxns((prev) => prev.map((t) => t.id === id ? { ...t, status: "approved" } : t));
    showToast("Transaction approved.");
  }

  // ── Cancel ────────────────────────────────────────────────────────────────
  async function cancelTransaction(id) {
    const { error } = await supabase.from("transactions").update({ status: "cancelled" }).eq("id", id);
    if (error) { showToast("Failed to cancel.", false); return; }
    setTxns((prev) => prev.map((t) => t.id === id ? { ...t, status: "cancelled" } : t));
    showToast("Transaction cancelled.");
  }

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const pending    = txns.filter((t) => t.status === "pending").length;
    const approved   = txns.filter((t) => t.status === "approved").length;
    const cancelled  = txns.filter((t) => t.status === "cancelled").length;
    const deposits   = txns.filter((t) => t.type === "deposit"    && t.status === "approved").reduce((s, t) => s + (t.amount ?? 0), 0);
    const withdrawals= txns.filter((t) => t.type === "withdrawal" && t.status === "approved").reduce((s, t) => s + (t.amount ?? 0), 0);
    return { pending, approved, cancelled, deposits, withdrawals, total: txns.length };
  }, [txns]);

  const STAT_CARDS = [
    { label: "Total",        value: stats.total,               icon: Receipt,        bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)",  bar: "var(--accent)" },
    { label: "Pending",      value: stats.pending,             icon: Clock,          bg: "rgba(251,191,36,0.15)",  color: "var(--amber)",        glow: "rgba(251,191,36,0.08)",  bar: "var(--amber)",  sub: "Awaiting review" },
    { label: "Approved",     value: stats.approved,            icon: CheckCircle2,   bg: "rgba(52,211,153,0.15)",  color: "var(--green)",        glow: "rgba(52,211,153,0.08)",  bar: "var(--green)" },
    { label: "Deposits",     value: fmtCurrency(stats.deposits),    icon: ArrowUpRight,   bg: "rgba(52,211,153,0.15)",  color: "var(--green)",        glow: "rgba(52,211,153,0.06)",  bar: "var(--green)",  sub: "Approved only" },
    { label: "Withdrawals",  value: fmtCurrency(stats.withdrawals), icon: ArrowDownRight, bg: "rgba(251,113,133,0.15)", color: "var(--rose)",         glow: "rgba(251,113,133,0.06)", bar: "var(--rose)",   sub: "Approved only" },
  ];

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = txns.filter((t) => {
      const matchSearch = !q
        || `${t.users?.first_name ?? ""} ${t.users?.last_name ?? ""}`.toLowerCase().includes(q)
        || (t.users?.email ?? "").toLowerCase().includes(q)
        || String(t.id).includes(q);
      const matchStatus = !statusFilter || t.status === statusFilter;
      const matchType   = !typeFilter   || t.type   === typeFilter;
      return matchSearch && matchStatus && matchType;
    });
    list.sort((a, b) => {
      if (sortField === "user")   { const an = `${a.users?.first_name}${a.users?.last_name}`; const bn = `${b.users?.first_name}${b.users?.last_name}`; return an.localeCompare(bn) * sortDir; }
      if (sortField === "amount") return ((a.amount ?? 0) - (b.amount ?? 0)) * sortDir;
      return (new Date(a.created_at ?? 0) - new Date(b.created_at ?? 0)) * sortDir;
    });
    return list;
  }, [txns, search, statusFilter, typeFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSort(field) {
    if (field === sortField) setSortDir((d) => d * -1);
    else { setSortField(field); setSortDir(-1); }
    setPage(1);
  }

  const COLS = [
    { key: "id",         label: "ID"      },
    { key: "user",       label: "User"    },
    { key: "type",       label: "Type"    },
    { key: "amount",     label: "Amount"  },
    { key: "status",     label: "Status"  },
    { key: "created_at", label: "Date"    },
  ];

  if (!sessionReady) {
    return (
      <div className="tx-gate">
        <StyleInjector />
        <div className="tx-gate-icon"><Receipt size={16} color="#fff" /></div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-sans)" }}>Verifying session…</p>
      </div>
    );
  }

  return (
    <div className="tx-shell">
      <StyleInjector />

      {/* ── Mobile sidebar overlay ── */}
      <div
        className={`ad-sidebar-overlay${sidebarOpen ? " open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Sidebar ── */}
      <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon">
            <CandlestickChart size={16} color="#fff" />
          </div>
          <div className="ad-logo-text">
            <strong>vantrexmarket</strong>
            <span>Admin panel</span>
          </div>
          <button
            className="ad-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            style={{
              marginLeft: "auto", display: "none",
              width: 28, height: 28, borderRadius: 7,
              background: "none", border: "1px solid var(--border)",
              color: "var(--text-muted)", cursor: "pointer",
              alignItems: "center", justifyContent: "center",
              flexShrink: 0, transition: "all 0.15s",
            }}
          >
            <X size={13} />
          </button>
        </div>

        <nav className="ad-nav" onClick={() => setSidebarOpen(false)}>
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="ad-nav-section-label">{section.label}</p>
              <div className="ad-nav-items">
                {section.items.map((item) => (
                  <SidebarLink key={item.to} {...item} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info">
              <strong>Super Admin</strong>
              <span>{adminEmail}</span>
            </div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Page ── */}
      <div className="tx-page">

        {/* Topbar */}
        <header className="tx-topbar">
          <div className="tx-topbar-left">
            <button className="tx-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="3" width="14" height="1.5" rx="0.75" fill="currentColor"/>
                <rect x="1" y="7.25" width="14" height="1.5" rx="0.75" fill="currentColor"/>
                <rect x="1" y="11.5" width="10" height="1.5" rx="0.75" fill="currentColor"/>
              </svg>
            </button>
            <div className="tx-topbar-badge"><Receipt size={9} />Transactions</div>
            <div>
              <div className="tx-topbar-title">Transaction management</div>
              <div className="tx-topbar-sub">Approve, cancel, or manually add transactions</div>
            </div>
          </div>
          <div className="tx-topbar-right">
            <button className="tx-btn" onClick={fetchTxns}><RefreshCw size={12} />Refresh</button>
            <button className="tx-btn-primary" onClick={() => setShowAdd(true)}><Plus size={12} />Add transaction</button>
          </div>
        </header>

        <main className="tx-main">

          {/* Stat cards */}
          <div className="tx-stat-grid">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
              <div key={label} className="tx-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                <div className="tx-stat-top">
                  <span className="tx-stat-label">{label}</span>
                  <div className="tx-stat-icon" style={{ background: bg }}>
                    <Icon size={14} style={{ color }} />
                  </div>
                </div>
                <div>
                  <div className="tx-stat-value">{value}</div>
                  {sub && <div className="tx-stat-sub">{sub}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Pending banner */}
          {stats.pending > 0 && (
            <div className="tx-pending-banner">
              <Clock size={14} style={{ color: "var(--amber)", flexShrink: 0 }} />
              <p>
                <strong>{stats.pending} transaction{stats.pending > 1 ? "s" : ""}</strong> awaiting approval.
              </p>
              <button className="tx-pending-link" onClick={() => { setStatusFilter("pending"); setPage(1); }}>
                Show pending →
              </button>
            </div>
          )}

          {/* Table panel */}
          <div className="tx-panel">

            {/* Toolbar */}
            <div className="tx-toolbar">
              <div className="tx-toolbar-left">
                <span className="tx-panel-title">All transactions</span>
                <span className="tx-count-pill">{filtered.length}</span>
              </div>
              <div className="tx-toolbar-right">
                <Filter size={12} style={{ color: "var(--text-muted)" }} />
                <select className="tx-select" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
                  <option value="">All types</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdrawal</option>
                </select>
                <select className="tx-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <div className="tx-search-wrap">
                  <Search size={12} className="tx-search-icon" />
                  <input
                    type="text" placeholder="Search user or ID…"
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="tx-search"
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="tx-table-wrap">
              <table className="tx-table">
                <thead>
                  <tr>
                    {COLS.map(({ key, label }) => (
                      <th key={key} onClick={() => handleSort(key)}>
                        {label}<SortIcon field={key} sortField={sortField} sortDir={sortDir} />
                      </th>
                    ))}
                    <th className="no-sort" style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <td key={j}><div className="tx-skel" style={{ width: `${45 + (i * j * 11) % 40}%` }} /></td>
                        ))}
                      </tr>
                    ))
                  ) : pageSlice.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="tx-empty">
                          <AlertCircle size={22} />
                          <span>No transactions match your filters</span>
                        </div>
                      </td>
                    </tr>
                  ) : pageSlice.map((t) => {
                    const aColor = avatarColor(t.user_id);
                    return (
                      <tr key={t.id}>
                        {/* ID */}
                        <td><span className="tx-id">#{t.id}</span></td>

                        {/* User */}
                        <td>
                          <div className="tx-avatar-cell">
                            <div className={`tx-avatar tx-avatar-${aColor}`}>
                              {initials(t.users?.first_name, t.users?.last_name)}
                            </div>
                            <div>
                              <div className="tx-cell-name">{t.users?.first_name} {t.users?.last_name}</div>
                              <div className="tx-cell-email">{t.users?.email}</div>
                            </div>
                          </div>
                        </td>

                        {/* Type */}
                        <td><TypeBadge type={t.type} /></td>

                        {/* Amount */}
                        <td>
                          <span className={t.type === "deposit" ? "tx-amount-pos" : "tx-amount-neg"}>
                            {t.type === "deposit" ? "+" : "−"}{fmtCurrency(t.amount)}
                            {t.currency && t.currency !== "USD" && (
                              <span style={{ fontSize: 10, fontWeight: 600, marginLeft: 4, opacity: 0.6 }}>{t.currency}</span>
                            )}
                          </span>
                        </td>

                        {/* Status */}
                        <td><StatusChip status={t.status} /></td>

                        {/* Date */}
                        <td><span className="tx-date">{fmtDate(t.created_at)}</span></td>

                        {/* Actions */}
                        <td>
                          <div className="tx-actions">
                            <button className="tx-icon-btn" onClick={() => setSelected(t)} title="View details">
                              <Eye size={12} />
                            </button>
                            {t.proof_url && (
                              <button className="tx-icon-btn" onClick={() => setSelected(t)} title="Has payment proof — view details to open it">
                                <Receipt size={12} style={{ color: "var(--accent-light)" }} />
                              </button>
                            )}
                            {t.status === "pending" && (
                              <>
                                <button className="tx-icon-btn approve" onClick={() => approveTransaction(t.id, t.user_id, t.wallet_id, t.amount, t.type)} title="Approve">
                                  <CheckCircle2 size={12} />
                                </button>
                                <button className="tx-icon-btn cancel" onClick={() => cancelTransaction(t.id)} title="Cancel">
                                  <XCircle size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="tx-pagination">
                <span className="tx-pag-info">
                  {filtered.length
                    ? `${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filtered.length)} of ${filtered.length}`
                    : "0 results"}
                </span>
                <div className="tx-pag-pages">
                  {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                    <button key={p} className={`tx-pag-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Detail modal */}
      {selected && (
        <DetailModal
          txn={selected}
          onClose={() => setSelected(null)}
          onApprove={approveTransaction}
          onCancel={cancelTransaction}
        />
      )}

      {/* Add modal */}
      {showAdd && (
        <AddModal
          onClose={() => setShowAdd(false)}
          onSuccess={(msg) => { showToast(msg); fetchTxns(); }}
        />
      )}

      {/* Toast */}
      {toast.msg && (
        <div className={`tx-toast ${toast.ok ? "ok" : "err"}`}>
          {toast.ok
            ? <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
            : <XCircle      size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}